// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// AUTO-GENERATED — do not edit manually.
// Source: docs/upstream-specs/data-model-schema.json
// Regenerate: python scripts/generate_entity_schemas.py

/* eslint-disable */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MeasurementGroup {
  label: string;
  fields: string[];
}

export interface EntitySchema {
  /** Human-readable label for display in the UI */
  label: string;
  /** Attribute field names from the TypeAttributes definition */
  attributeFields: string[];
  /** IRI from the OWL ontology (empty string if not defined) */
  ontologyIri: string;
}

// ---------------------------------------------------------------------------
// Measurement groups
//
// All ontology measurements organised by functional category.
// Use these to build consistent filter UIs across entity types.
// ---------------------------------------------------------------------------

export const MeasurementGroups: MeasurementGroup[] = [
  { label: 'Battery', fields: ['CellTemperature', 'SoC'] },
  {
    label: 'Electrical',
    fields: [
      'Current',
      'DCCurrent',
      'DCVoltage',
      'Frequency',
      'HarmonicsI',
      'HarmonicsV',
      'InputCurrent',
      'InputCurrentL1',
      'InputCurrentL2',
      'InputCurrentL3',
      'InputVoltageL1L2',
      'InputVoltageL1N',
      'InputVoltageL2L3',
      'InputVoltageL2N',
      'InputVoltageL3L1',
      'InputVoltageL3N',
      'InputVoltageLL',
      'InputVoltageLN',
      'OutputCurrent',
      'OutputCurrentL1',
      'OutputCurrentL2',
      'OutputCurrentL3',
      'OutputVoltageL1L2',
      'OutputVoltageL1N',
      'OutputVoltageL2L3',
      'OutputVoltageL2N',
      'OutputVoltageL3L1',
      'OutputVoltageL3N',
      'OutputVoltageLL',
      'OutputVoltageLN',
      'Voltage',
    ],
  },
  {
    label: 'Energy',
    fields: ['ActiveEnergy', 'EnergyAvailable', 'ExportActiveEnergy', 'ImportActiveEnergy'],
  },
  {
    label: 'Environment',
    fields: [
      'Chilled_Water_Return_Temperature_Sensor',
      'Chilled_Water_Supply_Temperature_Sensor',
      'EngineCoolantTemperature',
      'Entering_Condenser_Water_Temperature_Sensor',
      'Humidity',
      'Leaving_Condenser_Water_Temperature_Sensor',
      'Leaving_Condenser_Water_Temperature_Setpoint',
      'Mixed_Air_Temperature_Sensor',
      'Mixed_Air_Temperature_Setpoint',
      'Outside_Air_Temperature_Sensor',
      'Return_Air_Temperature_Sensor',
      'Supply_Air_Temperature_Sensor',
      'Supply_Air_Temperature_Setpoint',
      'Supply_Chilled_Water_Temperature_Setpoint',
      'Temperature',
      'Zone_Air_Temperature_Sensor',
    ],
  },
  { label: 'Generator', fields: ['EngineRPM', 'FuelLevel'] },
  {
    label: 'Power',
    fields: [
      'ActivePower',
      'ActivePowerSetpoint',
      'ApparentPower',
      'BypassActivePower',
      'ChargePowerSetpoint',
      'DischargePowerSetpoint',
      'ExportActivePower',
      'ExportActivePowerL1',
      'ExportActivePowerL2',
      'ExportActivePowerL3',
      'ImportActivePower',
      'ImportActivePowerL1',
      'ImportActivePowerL2',
      'ImportActivePowerL3',
      'InputApparentPower',
      'InputApparentPowerL1',
      'InputApparentPowerL2',
      'InputApparentPowerL3',
      'OutputApparentPower',
      'OutputApparentPowerL1',
      'OutputApparentPowerL2',
      'OutputApparentPowerL3',
      'PowerFactor',
      'ReactivePower',
    ],
  },
  {
    label: 'Setpoints & Control',
    fields: [
      'Chilled_Water_Flow_Setpoint',
      'Condenser_Water_Flow_Setpoint',
      'Speed_Setpoint',
      'Supply_Air_Flow_Setpoint',
    ],
  },
  {
    label: 'Other',
    fields: [
      'ATSTransferCount',
      'Chilled_Water_Flow_Sensor',
      'Condenser_Water_Flow_Sensor',
      'Electric_Power_Sensor',
      'EngineOilPressure',
      'LoadingPercentage',
      'MaxSOC',
      'MinSOC',
      'PercentZ',
      'Power_Sensor',
      'PrimaryKV',
      'RemainingTime',
      'SecondaryKV',
      'Supply_Air_Flow_Sensor',
    ],
  },
];

// ---------------------------------------------------------------------------
// Entity schemas
//
// One entry per entity type defined in the ontology.
// ---------------------------------------------------------------------------

export const EntitySchemas: Record<string, EntitySchema> = {
  ATSType: {
    label: 'Automatic Transfer Switch',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'transferTimeMS',
      'numberOfPoles',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'transferType',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  BESSType: {
    label: 'Battery Energy Storage System',
    attributeFields: [
      'ratedStorageCapacity',
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'hasValueStream',
      'batteryType',
      'batteryStringCount',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  BMSType: {
    label: 'Battery Management System',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'connectedTo',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  BatteryMetricsType: {
    label: 'Battery Metrics Type',
    attributeFields: [],
    ontologyIri: '',
  },
  BatteryStringMetricsType: {
    label: 'Battery String Metrics Type',
    attributeFields: [],
    ontologyIri: '',
  },
  BuswayType: {
    label: 'Busway and Bus Duct',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'protectedBy',
      'connectedTo',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  CellMetricsType: {
    label: 'Cell Metrics Type',
    attributeFields: [],
    ontologyIri: '',
  },
  ControllerType: {
    label: 'PLC Controls',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'communicationProtocol',
      'ipAddress',
      'controls',
      'connectedTo',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  DeviceType: {
    label: 'Device Type',
    attributeFields: [],
    ontologyIri: '',
  },
  ElectricalMeasurementsType: {
    label: 'Electrical Measurements Type',
    attributeFields: [],
    ontologyIri: '',
  },
  FeederType: {
    label: 'Feeder',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'fedBy',
      'feeds',
      'protectedBy',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  GatewayType: {
    label: 'Gateway / SCADA',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'communicationProtocol',
      'ipAddress',
      'connectedTo',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  GeneratorSetType: {
    label: 'Diesel/Gas Generator',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'fuelType',
      'feeds',
      'suppliesPowerTo',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'fuelTankCapacity',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  GroundingNetworkType: {
    label: 'Grounding & Bonding Network',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'protects',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  ITRackType: {
    label: 'IT Rack',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'fedBy',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'connectedTo',
      'hasLocation',
      'maxLoad',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  MCCType: {
    label: 'Motor Control Center',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'feeds',
      'fedBy',
      'protects',
      'controlledBy',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  MotorFeederType: {
    label: 'Motor Feeder',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'fedBy',
      'protectedBy',
      'controlledBy',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  PDUType: {
    label: 'Power Distribution Unit',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'circuitCount',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protectedBy',
      'protects',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  PQMeterType: {
    label: 'Power Quality Meter',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'connectedTo',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  PanelboardType: {
    label: 'Panelboard',
    attributeFields: [
      'ratedVoltage',
      'busbarRating',
      'breakerCount',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'hasLocation',
      'hasDocument',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  PowerDeviceType: {
    label: 'PowerDevice',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
    ],
    ontologyIri: '',
  },
  RPPType: {
    label: 'Remote Power Panel',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'circuitCount',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protectedBy',
      'protects',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  RackPDUType: {
    label: 'Rack Power Distribution Unit',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'circuitCount',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protectedBy',
      'protects',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'upstreamId',
      'outletCount',
    ],
    ontologyIri: '',
  },
  SPDType: {
    label: 'Surge Protective Device',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'protects',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  STSType: {
    label: 'Static Transfer Switch',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'transferTimeMS',
      'numberOfPoles',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'transferType',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  SiteType: {
    label: 'Site',
    attributeFields: [
      'latitude',
      'longitude',
      'siteId',
      'hasPointOfInterconnection',
      'hasValueStream',
      'containsEquipment',
      'isLocationOf',
      'ownedBy',
      'timeZone',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  SwitchgearType: {
    label: 'Switchgear',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'busbarRating',
      'breakerCount',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protects',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'hasValueStream',
      'breakerType',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  TransformerType: {
    label: 'Transformer',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  UPSType: {
    label: 'Uninterruptible Power Supply',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'feeds',
      'fedBy',
      'suppliesPowerTo',
      'suppliedBy',
      'primarySource',
      'backupSource',
      'protects',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'batteryType',
      'batteryStringCount',
      'upstreamId',
    ],
    ontologyIri: '',
  },
  UtilityMeterType: {
    label: 'Utility Meter',
    attributeFields: [
      'ratedPower',
      'ratedVoltage',
      'ratedCurrent',
      'manufacturer',
      'model',
      'serialNumber',
      'assetId',
      'hasSOP',
      'hasDocument',
      'feeds',
      'suppliesPowerTo',
      'protectedBy',
      'connectedTo',
      'controlledBy',
      'hasLocation',
      'hasValueStream',
      'upstreamId',
    ],
    ontologyIri: '',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return the display label for an entity type, falling back to the raw type name. */
export function getEntityLabel(entityType: string): string {
  return EntitySchemas[entityType]?.label ?? entityType;
}

/** Return measurement groups containing the given measurement name. */
export function getMeasurementGroups(measurementName: string): MeasurementGroup[] {
  return MeasurementGroups.filter(g => g.fields.includes(measurementName));
}
